import { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'

import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import type { PaginationState, SelectionModel, SortingState } from '@gto/ui/data-table'
import { EMPTY_SELECTION } from '@gto/ui/data-table'

import { useCreateCouponBatch } from '@/api/coupon-batch-hooks'
import { useBulkUpdateCoupons } from '@/api/coupon-bulk-hooks'
import {
  getListCouponTagsQueryKey,
  getListPartnerCouponsQueryKey,
  useCreateCoupon,
  useCreateCouponTag,
  useDeleteCouponTag,
  useListCouponTags,
  useUpdateCoupon,
} from '@/api/coupon-hooks'
import { usePartnerCouponPage } from '@/api/partner-coupon-hooks'
import { useSubscriptionPlans } from '@/api/subscription-plan-hooks'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { DataTable } from '@/components/data-table'
import { EmptyState } from '@/components/empty-state'
import { FacetChip } from '@/components/facet-chip'
import { SelectField } from '@/components/form'
import { InlineQueryError } from '@/components/inline-query-error'
import { MutationError } from '@/components/mutation-error'
import { TableFilterBar } from '@/components/table-filter-bar'
import {
  buildCouponBulkRequest,
  type CouponBulkEditValues,
  type CouponBulkOutcome,
  type CouponBulkStatusAction,
  toCouponBulkOutcome,
} from '@/domain/coupon-bulk'
import {
  batchScopeLabel,
  COUPON_PAGE_SIZES,
  couponListParams,
  type CouponStatusFilter,
  couponTagFilterValue,
  DEFAULT_COUPON_PAGE_SIZE,
  distinctCouponTags,
  hasCouponListNarrowing,
  isCouponSortField,
  isCouponStatus,
  parseCouponTagFilter,
  UNTAGGED_TAG_VALUE,
} from '@/domain/coupon-filters'
import {
  COUPON_STATUSES,
  type CouponStatus,
  ZERO_COUPON_STATUS_COUNTS,
} from '@/domain/coupon-status'
import type {
  AffiliateAdminCouponSchema,
  AffiliateCouponTagSchema,
  AffiliateCreateCouponTagRequest,
  CouponPlan,
} from '@/domain/coupons'
import { useClampPageIntoRange } from '@/hooks/use-clamp-page-into-range'
import { useDebouncedCallback } from '@/hooks/use-debounced-callback'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import type { CouponListSearch } from '@/utils/partner-detail-search-params'
import { toCouponListFilters } from '@/utils/partner-detail-search-params'
import { selectedRows } from '@/utils/table-selection'

import { CouponBatchResultsDialog } from './coupon-batch-results-dialog'
import { CouponBulkBar } from './coupon-bulk-bar'
import { CouponBulkEditDialog } from './coupon-bulk-edit-dialog'
import { CouponCreateDialog } from './coupon-create-dialog'
import { CouponEditDialog } from './coupon-edit-dialog'
import { CouponFormError } from './coupon-form-error'
import { CouponTagPill } from './coupon-tag-pill'
import { useCouponColumns } from './use-coupon-columns'

// Stable empty references so the derivations below don't recompute on every
// render while a query is loading (`?? []` mints a fresh array each time).
const NO_COUPONS: AffiliateAdminCouponSchema[] = []
const NO_PLANS: CouponPlan[] = []
const NO_TAGS: AffiliateCouponTagSchema[] = []

// Narrow the toggle's raw string to the status union without an `as` cast
// (`as` is banned in app code — see data-table.tsx). Anything unexpected
// (including the empty deselect value) collapses to the safe "all" default.
const asStatus = (value: string): CouponStatusFilter => (isCouponStatus(value) ? value : 'all')

// Toggle labels live in the message catalogue rather than reusing StatusBadge's
// internal label map, which is not translated.
const STATUS_FILTER_LABEL_IDS: Record<CouponStatus, string> = {
  INACTIVE: 'admin.partnerDetail.coupons.status.inactive',
  EXPIRED: 'admin.partnerDetail.coupons.status.expired',
  USED_UP: 'admin.partnerDetail.coupons.status.usedUp',
  PENDING: 'admin.partnerDetail.coupons.status.pending',
  ACTIVE: 'admin.partnerDetail.coupons.status.active',
}

/**
 * The two status changes the bulk bar offers as one-click shortcuts. `keep` is
 * excluded: it is the edit dialog's "leave unchanged" option and has nothing to
 * confirm.
 */
type BulkStatusShortcut = Extract<CouponBulkStatusAction, 'activate' | 'deactivate'>

const BULK_CONFIRM_IDS: Record<
  BulkStatusShortcut,
  { title: string; description: string; confirm: string }
> = {
  activate: {
    title: 'admin.partnerDetail.coupons.bulk.confirmActivateTitle',
    description: 'admin.partnerDetail.coupons.bulk.confirmActivateDescription',
    confirm: 'admin.partnerDetail.coupons.activate',
  },
  deactivate: {
    title: 'admin.partnerDetail.coupons.bulk.confirmDeactivateTitle',
    description: 'admin.partnerDetail.coupons.bulk.confirmDeactivateDescription',
    confirm: 'admin.partnerDetail.coupons.deactivate',
  },
}

/**
 * Result of the last bulk action. A partial failure names the codes that did
 * NOT get written — an admin who just deactivated 40 links needs to know which
 * three are still live, and "some updates failed" does not tell them.
 */
const BulkOutcomeAlert = ({ outcome }: { outcome: CouponBulkOutcome }) => {
  if (outcome.kind === 'none') {
    return (
      <Alert
        data-tst="coupon-bulk-outcome"
        severity="info"
      >
        <FormattedMessage id="admin.partnerDetail.coupons.bulk.noneAlert" />
      </Alert>
    )
  }

  if (outcome.kind === 'success') {
    return (
      <Alert
        data-tst="coupon-bulk-outcome"
        severity="success"
      >
        <FormattedMessage
          id="admin.partnerDetail.coupons.bulk.successAlert"
          values={{ count: outcome.updated }}
        />
      </Alert>
    )
  }

  return (
    <Alert
      data-tst="coupon-bulk-outcome"
      severity="warning"
    >
      <FormattedMessage
        id="admin.partnerDetail.coupons.bulk.partialAlert"
        values={{
          updated: outcome.updated,
          failed: outcome.failedCodes.length,
          codes: outcome.failedCodes.join(', '),
        }}
      />
    </Alert>
  )
}

type CouponsTabProps = {
  partnerId: string
  /**
   * The view the table renders: tag scope, status, batch, code search, sort and
   * page. Controlled by the page, which keeps it in the URL — every one of them
   * is a SERVER query param, so they belong to the shareable view rather than to
   * this component's local state.
   */
  search: CouponListSearch
  /**
   * `resetPage` mirrors the partners list: any narrowing returns to page 1 unless
   * the caller opts out, which only paging itself does.
   */
  onSearchChange: (delta: Partial<CouponListSearch>, opts?: { resetPage?: boolean }) => void
}

export const CouponsTab = ({ partnerId, search, onSearchChange }: CouponsTabProps) => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [editCoupon, setEditCoupon] = useState<AffiliateAdminCouponSchema | null>(null)
  const [batchResults, setBatchResults] = useState<AffiliateAdminCouponSchema[] | null>(null)
  const [selection, setSelection] = useState<SelectionModel>(EMPTY_SELECTION)
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const [pendingBulkStatus, setPendingBulkStatus] = useState<BulkStatusShortcut | null>(null)
  const [bulkOutcome, setBulkOutcome] = useState<CouponBulkOutcome | null>(null)
  const { hasError, onError, clearError } = useMutationErrorHandler('coupon mutation failed: ')

  const filters = toCouponListFilters(search)
  /**
   * The selection belongs to the rows on screen, so it resets when the request that
   * produced them changes — a page, a filter, a sort. DataGridPro pruned selected ids as
   * their rows left; the DS keeps whatever it was handed, which would leave the header
   * checkbox indeterminate on a page where nothing is ticked, and resurrect the old ticks
   * on the way back.
   *
   * Keyed on the REQUEST (the same params that key the query) and adjusted DURING render —
   * React's own recipe for resetting state when an input changes, and the one the code
   * draft below already follows. Clearing inside every URL writer instead would leave a
   * dozen call sites that each have to remember, and would still miss the invalidation
   * case: a bulk edit re-selects exactly the links that FAILED, and because an
   * invalidation does not move these params, that selection correctly survives here.
   */
  const rowSetKey = JSON.stringify(couponListParams(filters))
  const [lastRowSetKey, setLastRowSetKey] = useState(rowSetKey)
  if (lastRowSetKey !== rowSetKey) {
    setLastRowSetKey(rowSetKey)
    setSelection(EMPTY_SELECTION)
  }
  const { tagFilter } = filters
  // ONE page, ONE request. Every filter, the ordering and the page index ride the
  // query string, so a code on page 7 is reachable from the search box, a chosen
  // tag returns matches from every page, and a header click reorders the whole
  // set rather than the ten rows in hand.
  const couponsQuery = usePartnerCouponPage(partnerId, filters)
  const {
    data: couponPage,
    dataUpdatedAt,
    isLoading,
    isError,
    isPlaceholderData,
    refetch,
  } = couponsQuery
  const rows = couponPage?.coupons ?? NO_COUPONS
  const total = couponPage?.total ?? 0
  // The pills' counts come from the response, which spans every active filter
  // EXCEPT status — so they describe the partner's whole matching set and hold
  // still as the admin clicks between rungs.
  const statusCounts = couponPage?.status_counts ?? ZERO_COUPON_STATUS_COUNTS

  // Forward load failures to Sentry — the bare app QueryClient has no global
  // sink. Previously a failed coupons query fell through to the EmptyState
  // ("No coupons for this partner."), masking the failure as a legitimate
  // empty list and losing the observability signal.
  useReportQueryError(couponsQuery, 'usePartnerCouponPage failed: ')

  // A `?page=` past the end comes back empty for a set that is not empty; walk the URL
  // back to the last page that holds rows. `resetPage: false` — the whole point is to
  // land on THAT page, and page 1 drops the key rather than pinning the default.
  useClampPageIntoRange({
    page: filters.page,
    pageSize: filters.pageSize,
    total,
    loading: isLoading || isPlaceholderData,
    onClamp: (page) =>
      onSearchChange({ page: page === 1 ? undefined : page }, { resetPage: false }),
  })

  // The code search is the one filter that must not write straight through: a
  // keystroke is now a request. The box renders a local draft and pushes it into
  // the URL — and with it the query key — only once the typist pauses. Mirrors
  // the admin partners list.
  const committedCode = search.code ?? ''
  const [codeDraft, setCodeDraft] = useState(committedCode)
  // The draft also has to follow a URL this tab did not write — back/forward, a
  // deep link, the tag-delete handler clearing filters. Adjusted DURING render
  // (React's own recipe for resetting state when an input changes) rather than in
  // an effect, which would paint the stale draft for a frame first.
  const [lastCommittedCode, setLastCommittedCode] = useState(committedCode)
  if (lastCommittedCode !== committedCode) {
    setLastCommittedCode(committedCode)
    setCodeDraft(committedCode)
  }
  const { debounced: writeCodeDraft } = useDebouncedCallback((value: string) => {
    onSearchChange({ code: value === '' ? undefined : value })
  }, 300)

  // ONE instant for every row's status badge: `deriveCouponStatus` defaults `now`
  // to its own `new Date()`, so leaving each cell to it dates the rows in one
  // table against different clocks. Anchored to the fetch rather than to render,
  // which also keeps it a value the React Compiler can memoize behind — a bare
  // `new Date()` in the body is fresh every render and prunes every scope below
  // it, handing `DataGridPro` new arrays on each keystroke. `dataUpdatedAt` is 0
  // while a `keepPreviousData` window shows the outgoing page, where it does not
  // yet describe what is on screen.
  const now = useMemo(
    () => (dataUpdatedAt === 0 ? new Date() : new Date(dataUpdatedAt)),
    [dataUpdatedAt],
  )
  // Buckets worth offering: the ones the server counted, plus whatever is
  // selected right now (so narrowing to zero matches can't make the active
  // toggle disappear and strand the admin on an invisible filter).
  const statusFilterOptions = COUPON_STATUSES.filter(
    (status) => statusCounts[status] > 0 || status === filters.status,
  )
  // A name for the active batch scope, read off the loaded page — every row on it
  // shares that batch, so its prefix names it. Skipped while the previous page is
  // still on screen, where the rows belong to the OUTGOING scope and would name
  // the wrong batch.
  const batchScope =
    filters.batchId === ''
      ? null
      : ((isPlaceholderData ? null : batchScopeLabel(rows)) ??
        formatMessage({ id: 'admin.partnerDetail.coupons.batchScopeUnknown' }))
  const hasScopedCoupon = rows.some((c) => c.plan_ids.length > 0)

  // Every bulk write below is built from `selectedCoupons`, never from the raw model — see
  // `selectedRows` for why the table cannot be trusted to prune it.
  const selectedCoupons = selectedRows(selection, rows, (row) => row.id)
  // Distinct tags the selection actually carries — the only ones a bulk removal
  // could act on. Read off the rows, not the partner's tag list, so a removal is
  // never offered for a tag no selected link has.
  const selectionTags = distinctCouponTags(selectedCoupons)

  // The plan catalog powers the scope selector in the dialogs AND the Scope
  // column's plan names. Only fetch it when something actually needs it: a
  // dialog is open, or the partner has at least one scoped link to name.
  // Its failure disables scoped creation ("All plans" still works) and the
  // Scope column falls back to a plan count — a soft failure, still reported.
  const plansQuery = useSubscriptionPlans({
    enabled: createOpen || editCoupon !== null || hasScopedCoupon,
  })
  useReportQueryError(plansQuery, 'useSubscriptionPlans failed: ')
  // React Query hands back a stable `data` ref until it changes, and the
  // module-level `NO_PLANS` covers the loading/absent case — so `plans` stays
  // referentially stable without a `useMemo`, and the edit dialog's scope-
  // reconstruct effect runs once on load rather than on every incidental render.
  const plans = plansQuery.data ?? NO_PLANS
  const plansLoading = plansQuery.isLoading
  const plansError = plansQuery.isError

  // Always enabled, unlike the plan catalog: this is the partner's WHOLE tag
  // list, and it is what the tag facet offers — sourcing the facet from the
  // loaded rows is the defect being fixed, since a paginated list can only ever
  // show the current page's tags. The dialogs' tag pickers read the same query.
  const tagsQuery = useListCouponTags(partnerId)
  useReportQueryError(tagsQuery, 'useListCouponTags failed: ')
  const tags = tagsQuery.data ?? NO_TAGS
  const tagFilterValue = couponTagFilterValue(tagFilter)
  // A `?tag=` deep link (or a bookmark) can name a tag that has since been
  // deleted. Offer it explicitly rather than leaving the trigger blank: the
  // filter is live either way, and a blank trigger reads as "not filtering".
  // Gated on settled rather than successful: a list still in flight must not mislabel
  // a real tag, but a list whose fetch FAILED never becomes successful, and gating on
  // success there would withhold this option for good — leaving exactly the blank
  // trigger described above while the load is genuinely tag-scoped.
  const selectedTagUnknown =
    tagFilter.kind === 'tag' &&
    !tagsQuery.isPending &&
    !tags.some((tag) => tag.id === tagFilter.tagId)

  const invalidateCoupons = () =>
    void queryClient.invalidateQueries({ queryKey: getListPartnerCouponsQueryKey(partnerId) })
  const createCoupon = useCreateCoupon({
    mutation: {
      onMutate: clearError,
      onSuccess: () => {
        invalidateCoupons()
        setCreateOpen(false)
      },
      onError,
    },
  })
  const createBatch = useCreateCouponBatch({
    mutation: {
      onMutate: clearError,
      onSuccess: (generated) => {
        invalidateCoupons()
        setCreateOpen(false)
        setBatchResults(generated)
      },
      onError,
    },
  })
  const updateCoupon = useUpdateCoupon({
    mutation: {
      onMutate: clearError,
      onSuccess: () => {
        invalidateCoupons()
        setEditCoupon(null)
      },
      onError,
    },
  })
  // A 200 that carries `failed[]` is already reported to Sentry by the hook
  // (React Query's `onError` never fires for a 2xx, so nothing here would).
  // Rendering the outcome is therefore all this call site owes for that case —
  // routing it through `onError` too would log the one event twice. `onError`
  // stays for a genuine transport failure, which does reject.
  const bulkUpdate = useBulkUpdateCoupons({
    mutation: {
      onMutate: () => {
        clearError()
        setBulkOutcome(null)
      },
      onSuccess: (result) => {
        invalidateCoupons()
        setBulkEditOpen(false)
        setPendingBulkStatus(null)

        setBulkOutcome(toCouponBulkOutcome(result, selectedCoupons))
        // Leave exactly the links that failed selected, so the obvious next
        // action — press the button again — retries only those. An empty
        // `failed` therefore clears the selection.
        setSelection({
          mode: 'loaded',
          selectedIds: Object.fromEntries(
            result.failed.map((failure) => [failure.coupon_id, true]),
          ),
        })
      },
      onError,
    },
  })

  const applyBulkEdit = (values: CouponBulkEditValues) => {
    const couponIds = selectedCoupons.map((coupon) => coupon.id)

    // The bar and both dialogs only exist with a non-empty selection, so this is
    // a guard against a future caller, not a reachable state — the endpoint
    // rejects an empty `coupon_ids` rather than treating it as "nothing to do".
    if (couponIds.length === 0) {
      return
    }

    bulkUpdate.mutate({ partnerId, data: buildCouponBulkRequest(couponIds, values) })
  }

  const createTagError = useMutationErrorHandler('create coupon tag failed: ')
  const createTag = useCreateCouponTag({
    mutation: {
      onMutate: createTagError.clearError,
      onSuccess: (createdTag) => {
        queryClient.setQueryData<AffiliateCouponTagSchema[]>(
          getListCouponTagsQueryKey(partnerId),
          (currentTags) =>
            [...(currentTags ?? []), createdTag].sort((left, right) =>
              left.name.localeCompare(right.name),
            ),
        )
      },
      onError: createTagError.onError,
    },
  })
  const deleteTagError = useMutationErrorHandler('delete coupon tag failed: ')
  const deleteTag = useDeleteCouponTag({
    mutation: {
      onSuccess: (_response, variables) => {
        queryClient.setQueryData<AffiliateCouponTagSchema[]>(
          getListCouponTagsQueryKey(partnerId),
          (currentTags) => currentTags?.filter((tag) => tag.id !== variables.tagId),
        )
        setEditCoupon((currentCoupon) =>
          currentCoupon === null
            ? null
            : {
                ...currentCoupon,
                tags: currentCoupon.tags.filter((tag) => tag.id !== variables.tagId),
              },
        )
        // The scope the admin was viewing no longer exists — drop it rather than
        // leaving the list pinned to a dead tag that now matches nothing.
        if (tagFilter.kind === 'tag' && tagFilter.tagId === variables.tagId) {
          onSearchChange({ tag: undefined })
        }
        invalidateCoupons()
      },
      onError: deleteTagError.onError,
    },
  })
  const tagState = {
    tags,
    tagsLoading: tagsQuery.isLoading,
    tagsError: tagsQuery.isError,
    creatingTag: createTag.isPending,
    createTagError: createTagError.hasError,
    createTagServerError: createTag.error,
    deletingTag: deleteTag.isPending,
    deleteTagError: deleteTagError.hasError,
    onCreateTag: async (data: AffiliateCreateCouponTagRequest) => {
      createTag.reset()
      createTagError.clearError()
      try {
        return await createTag.mutateAsync({
          partnerId,
          data,
        })
      } catch {
        return null
      }
    },
    onDeleteTag: async (tagIdToDelete: string) => {
      deleteTag.reset()
      deleteTagError.clearError()
      try {
        await deleteTag.mutateAsync({ partnerId, tagId: tagIdToDelete })

        return true
      } catch {
        return false
      }
    },
  }

  const closeCreate = () => {
    setCreateOpen(false)
    createCoupon.reset()
    createBatch.reset()
    createTag.reset()
    createTagError.clearError()
    deleteTag.reset()
    deleteTagError.clearError()
    clearError()
  }
  const closeEdit = () => {
    setEditCoupon(null)
    updateCoupon.reset()
    createTag.reset()
    createTagError.clearError()
    deleteTag.reset()
    deleteTagError.clearError()
    clearError()
  }

  const columns = useCouponColumns({
    partnerId,
    plans,
    now,
    onError,
    clearError,
    onBatchFilter: (nextBatchId) => onSearchChange({ batch: nextBatchId }),
  })

  // Every filter is a server query, so any of them can legitimately empty the row
  // set. The "this partner has no links" state therefore only applies when
  // nothing is narrowing the load — otherwise the empty state would replace (and
  // unmount) the very control that emptied the list, stranding the admin on a
  // filter they can no longer clear. A filtered miss is a no-matches state,
  // filter bar intact. Keyed on `total` rather than the rows in hand: a stale
  // `?page=` past the end also comes back empty, and that is not "no links".
  const isNarrowed = hasCouponListNarrowing(filters)
  // Empty states wait for the SETTLED envelope: `keepPreviousData` serves the outgoing
  // one, so mid-change `total` still describes the filter the admin just left.
  // `!isError` too: a failed load also reports total 0, and an empty state claiming the
  // partner has no links would be wrong. Today the JSX checks `isError` first, so this is
  // belt-and-braces — but it makes the predicate true by construction rather than by the
  // order of the branches below it.
  const settled = !isLoading && !isPlaceholderData && !isError
  const partnerHasNoLinks = settled && total === 0 && !isNarrowed
  // Both empty states key on the server's count, never on the rows in hand: an
  // out-of-range `?page=` returns no rows for a set that is not empty, and calling
  // that "no matches" is wrong AND unmounts the grid — which is what would have
  // clamped the page back into range and repaired the URL.
  const noMatches = settled && total === 0 && !partnerHasNoLinks
  // The bar deliberately does NOT wait for `settled`: every filter change opens a
  // placeholder window, and hiding it through each one would unmount the control the
  // admin is mid-interaction with. A stale non-zero total is the right answer here.
  const showFilterBar = !isLoading && (total > 0 || isNarrowed || isPlaceholderData)

  // The table is 0-based, the endpoint (and the URL) 1-based.
  const paginationModel: PaginationState = {
    pageIndex: filters.page - 1,
    pageSize: filters.pageSize,
  }
  // Always a model, never empty: the endpoint orders by `-created_at` by default,
  // so showing that column's indicator describes the order the rows are in —
  // clearing the sort returns here rather than to no order at all.
  const sortModel: SortingState = [{ id: filters.sort.field, desc: filters.sort.dir === 'desc' }]

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row justify-end">
        <Button
          data-tst="coupon-create"
          size="small"
          variant="contained"
          onClick={() => setCreateOpen(true)}
        >
          <FormattedMessage id="admin.partnerDetail.coupons.create" />
        </Button>
      </div>

      <MutationError
        messageId="admin.partnerDetail.coupons.error"
        show={hasError}
      />

      {isError ? (
        <InlineQueryError
          data-tst="partner-coupons-error"
          messageId="admin.partnerDetail.coupons.loadError"
          onRetry={() => void refetch()}
        />
      ) : partnerHasNoLinks ? (
        <EmptyState title={<FormattedMessage id="admin.partnerDetail.coupons.empty" />} />
      ) : (
        <>
          {showFilterBar && (
            <TableFilterBar
              search={codeDraft}
              value={filters.status}
              additionalFilters={
                <>
                  {/* Offered once the partner has any tag to pick — and always
                      while a tag scope is active, so the control that set it can
                      clear it. No per-tag counts: the facet spans the partner's
                      whole tag list while the rows are one page. */}
                  {(tags.length > 0 || tagFilter.kind !== 'any') && (
                    <SelectField
                      ariaLabel={formatMessage({ id: 'admin.partnerDetail.coupons.tag' })}
                      className="min-w-40"
                      value={tagFilterValue}
                      options={[
                        {
                          value: '',
                          label: formatMessage({ id: 'admin.partnerDetail.coupons.allTags' }),
                        },
                        {
                          value: UNTAGGED_TAG_VALUE,
                          label: formatMessage({ id: 'admin.partnerDetail.coupons.untagged' }),
                        },
                        ...tags.map((tag) => ({
                          value: tag.id,
                          label: (
                            <CouponTagPill
                              compact
                              tag={tag}
                            />
                          ),
                        })),
                        ...(selectedTagUnknown
                          ? [
                              {
                                value: tagFilterValue,
                                label: formatMessage({
                                  id: 'admin.partnerDetail.coupons.unknownTag',
                                }),
                              },
                            ]
                          : []),
                      ]}
                      onValueChange={(nextValue) =>
                        onSearchChange({
                          tag: couponTagFilterValue(parseCouponTagFilter(nextValue)) || undefined,
                        })
                      }
                    />
                  )}
                  {/* A chip rather than a dropdown of every batch: nothing
                      enumerates a partner's batches server-side, so a facet list
                      could only ever offer the batches on the loaded page and
                      count them wrong. The scope is set from a row's action menu
                      (or a shared link) and cleared here. */}
                  {batchScope !== null && (
                    <FacetChip
                      dataTst="coupon-batch-chip"
                      labelId="admin.partnerDetail.coupons.batch"
                      value={batchScope}
                      onRemove={() => onSearchChange({ batch: undefined })}
                    />
                  )}
                </>
              }
              options={[
                {
                  value: 'all',
                  label: formatMessage({ id: 'admin.partnerDetail.coupons.status.all' }),
                  count: statusCounts.all,
                },
                // Only the buckets this partner actually has — five permanently
                // visible toggles, three of them reading 0, is noise. The active
                // filter is kept even at 0 so the toggle can't vanish under the
                // user when the last matching row is edited away.
                ...statusFilterOptions.map((status) => ({
                  value: status,
                  label: formatMessage({ id: STATUS_FILTER_LABEL_IDS[status] }),
                  count: statusCounts[status],
                })),
              ]}
              searchPlaceholder={formatMessage({
                id: 'admin.partnerDetail.coupons.searchPlaceholder',
              })}
              onSearchChange={(nextCode) => {
                setCodeDraft(nextCode)
                writeCodeDraft(nextCode)
              }}
              onValueChange={(status) => {
                const next = asStatus(status)
                onSearchChange({ status: next === 'all' ? undefined : next })
              }}
            />
          )}

          {bulkOutcome !== null && <BulkOutcomeAlert outcome={bulkOutcome} />}

          {selectedCoupons.length > 0 && (
            <CouponBulkBar
              busy={bulkUpdate.isPending}
              selectedCount={selectedCoupons.length}
              onActivate={() => setPendingBulkStatus('activate')}
              onClear={() => setSelection(EMPTY_SELECTION)}
              onDeactivate={() => setPendingBulkStatus('deactivate')}
              onEdit={() => setBulkEditOpen(true)}
            />
          )}

          {noMatches ? (
            <EmptyState title={<FormattedMessage id="admin.partnerDetail.coupons.noMatches" />} />
          ) : (
            <DataTable<AffiliateAdminCouponSchema>
              manualPagination
              manualSorting
              columns={columns}
              data={rows}
              getRowId={(row) => row.id}
              // Two distinct signals, deliberately not merged: `isLoading` is the first
              // load (skeleton, no rows yet), while `isPlaceholderData` is a page or filter
              // change still in flight over the OUTGOING rows. Folding the second into
              // `loading` would claim a first load on every page click and, worse, hide the
              // staleness from the DS — which gates selection on it, so a select-all during
              // the window can't bind to rows about to be replaced.
              loading={isLoading}
              // Same constant the URL schema validates `?size=` against, rather than
              // agreeing with the wrapper's default by comment: the pager renders no
              // option for a size it was not offered, so the two drifting apart would
              // strand the pager on a blank value for an otherwise-accepted URL.
              pageSizeOptions={[...COUPON_PAGE_SIZES]}
              pagination={paginationModel}
              refetching={isPlaceholderData}
              // The engine holds ONE page under `manualPagination`, so it cannot count the
              // result set — this is the only truthful total the pager can size itself from.
              rowCount={total}
              sorting={sortModel}
              // DataGridPro's cycle, restored through the engine-option escape hatch (no
              // DS prop covers either half):
              //   `enableSortingRemoval` — two directions, no unsorted third. TanStack's
              //   default cycle ends in an EMPTY model, which would make the first click
              //   on the already-sorted `created_at` a no-op that drops aria-sort.
              //   `sortDescFirst` — ascending first on EVERY column. TanStack infers the
              //   opener from the value type (descending for the Date and numeric
              //   accessors), so Expiry and Discount would open the other way round from
              //   Code and Created for no reason the admin can see.
              tableOptions={{ enableSortingRemoval: false, sortDescFirst: false }}
              selection={{
                model: selection,
                onSelectionChange: (next) => {
                  setSelection(next)
                  // The previous run's result no longer describes this selection.
                  setBulkOutcome(null)
                },
                selectAllLabel: formatMessage({ id: 'common.table.selectAll' }),
                selectRowLabel: formatMessage({ id: 'common.table.selectRow' }),
              }}
              // The wrapper ignores clicks originating inside an interactive control, so
              // ticking a row never opens the edit dialog this handler owns.
              onRowClick={(row) => setEditCoupon(row)}
              onPaginationChange={(next) => {
                // A page-size change re-slices the whole list, so the current
                // index no longer points at the same rows — back to page 1 (the
                // default `resetPage`). Paging itself keeps the index it just set.
                if (next.pageSize !== filters.pageSize) {
                  onSearchChange({
                    size: next.pageSize === DEFAULT_COUPON_PAGE_SIZE ? undefined : next.pageSize,
                  })

                  return
                }
                onSearchChange(
                  { page: next.pageIndex === 0 ? undefined : next.pageIndex + 1 },
                  { resetPage: false },
                )
              }}
              onSortingChange={(next) => {
                const [first] = next

                if (first === undefined || !isCouponSortField(first.id)) {
                  // Sort cleared (or on a column the endpoint cannot order by):
                  // drop both keys so the list returns to the BE default.
                  onSearchChange({ sort: undefined, dir: undefined })

                  return
                }
                // `desc` is a boolean here, so the direction is total — no runtime
                // guard needed, unlike DataGridPro's nullable `sort`.
                onSearchChange({ sort: first.id, dir: first.desc ? 'desc' : 'asc' })
              }}
            />
          )}
        </>
      )}

      <CouponCreateDialog
        batchError={createBatch.error}
        open={createOpen}
        plans={plans}
        plansError={plansError}
        plansLoading={plansLoading}
        singleError={createCoupon.error}
        submitting={createCoupon.isPending || createBatch.isPending}
        tagState={tagState}
        onClose={closeCreate}
        onSubmitBatch={(data) => createBatch.mutate({ partnerId, data })}
        onSubmitSingle={(data) => createCoupon.mutate({ partnerId, data })}
      />

      {editCoupon && (
        <CouponEditDialog
          coupon={editCoupon}
          plans={plans}
          plansError={plansError}
          plansLoading={plansLoading}
          serverError={updateCoupon.error}
          submitting={updateCoupon.isPending}
          tagState={tagState}
          onClose={closeEdit}
          onSubmit={(data) => updateCoupon.mutate({ partnerId, couponId: editCoupon.id, data })}
        />
      )}

      {bulkEditOpen && (
        <CouponBulkEditDialog
          removableTags={selectionTags}
          selectedCount={selectedCoupons.length}
          serverError={bulkUpdate.error}
          submitting={bulkUpdate.isPending}
          tagState={tagState}
          onClose={() => setBulkEditOpen(false)}
          onSubmit={applyBulkEdit}
        />
      )}

      {/* Both shortcuts are reversible `is_active` writes, but they land on
          every selected link at once and a deactivation is immediately visible
          to customers holding the code — worth one confirmation step. */}
      {pendingBulkStatus !== null && (
        <ConfirmDialog
          open
          cancelLabel={<FormattedMessage id="admin.common.cancel" />}
          confirmColor={pendingBulkStatus === 'deactivate' ? 'error' : 'primary'}
          confirmLabel={<FormattedMessage id={BULK_CONFIRM_IDS[pendingBulkStatus].confirm} />}
          description={<FormattedMessage id={BULK_CONFIRM_IDS[pendingBulkStatus].description} />}
          loading={bulkUpdate.isPending}
          title={
            <FormattedMessage
              id={BULK_CONFIRM_IDS[pendingBulkStatus].title}
              values={{ count: selectedCoupons.length }}
            />
          }
          onCancel={() => setPendingBulkStatus(null)}
          onConfirm={() =>
            applyBulkEdit({ addTagIds: [], removeTagIds: [], status: pendingBulkStatus })
          }
        >
          {/* Inside the confirm, not on the page behind it: this modal covers the
              tab's own error alert, so a rejected write would otherwise only
              re-enable the button and say nothing. */}
          <CouponFormError serverError={bulkUpdate.error} />
        </ConfirmDialog>
      )}

      <CouponBatchResultsDialog
        coupons={batchResults ?? []}
        open={batchResults !== null}
        onClose={() => setBatchResults(null)}
      />
    </div>
  )
}
